import { useEffect, useMemo, useState } from 'react';
import {
  EllipsisHorizontalIcon, EllipsisVerticalIcon, PlusIcon, MagnifyingGlassIcon, XMarkIcon,
  ArchiveBoxIcon, TrashIcon, PencilIcon, ShareIcon, ArchiveBoxXMarkIcon,
  UserIcon, UserGroupIcon, BookOpenIcon, ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { listChats, patchChat, deleteChat, deleteChatsBulk, type Chat } from '../../api/unimedica';
import { useDialog } from '../../contexts/ConfirmContext';
import { modeLabel, chatTitleDisplay, patientLabelFromId, sortChatsWithChildren, relTime } from '../../util/chat';

/**
 * Linke Spalte des Chats — Port aus chat/ChatSidebar.svelte.
 * Chats nach Bereich gruppiert (Patienten / Alle Patienten / Wissen /
 * Allgemein), Akten-Chats zusaetzlich je Patient gebuendelt — die
 * Ueberschrift beantwortet „worueber?", der Titel den Rest.
 * Review-Chats erscheinen NICHT hier, sie leben im Eingang.
 */
// 10.10.2026 (Christian): Art des Chats auf einen Blick — je Bereich eigenes Symbol und eigene Farbe.
const BEREICHE = [
  { key: 'patient', label: 'Patientenakten', icon: UserIcon, kopf: 'bg-primary-50 text-primary-800 border-primary-100' },
  { key: 'praxis', label: 'Alle Akten', icon: UserGroupIcon, kopf: 'bg-violet-50 text-violet-800 border-violet-100' },
  { key: 'wissen', label: 'Wissen', icon: BookOpenIcon, kopf: 'bg-emerald-50 text-emerald-800 border-emerald-100' },
  { key: 'general', label: 'Allgemein', icon: ChatBubbleLeftRightIcon, kopf: 'bg-gray-100 text-ps-dark-700 border-gray-200' },
];
const patientLabel = (c: Chat & { patient_name?: string }) => {
  const n = (c.patient_name ?? '').trim();
  if (n) return n;
  const teile = String(c.patient_id ?? '').split('_');
  return teile.length >= 3 ? `${teile[1]}, ${teile.slice(2).join(' ')}` : (c.patient_id || 'Ohne Akte');
};
const modeBadgeCls = (m: string) => ({ patient: 'badge-brand', patient_md: 'badge-brand', wissen: 'badge-ok', praxis: '!border-violet-200 !bg-violet-50 !text-violet-700' } as Record<string, string>)[m] ?? 'badge-neutral';
const praxisId = (c: Chat) => { const id = String(c.patient_id ?? '').split('_')[0]; return /^\d+$/.test(id) ? id : ''; };

export default function ChatSidebar({ chats, activeChat, showArchived, mobileSidebarOpen, onChatsChanged, onShowArchivedChanged, onMobileSidebarOpenChanged, onSelectChat, onNewChat, onOpenShareModal, onActiveChatRemoved }: {
  chats: Chat[]; activeChat: Chat | null; showArchived: boolean; mobileSidebarOpen: boolean;
  onChatsChanged: (c: Chat[]) => void; onShowArchivedChanged: (v: boolean) => void; onMobileSidebarOpenChanged: (v: boolean) => void;
  onSelectChat: (c: Chat) => void; onNewChat: () => void; onOpenShareModal: (c: Chat, e?: React.MouseEvent) => void; onActiveChatRemoved: () => void;
}) {
  const { confirm } = useDialog();
  const [chatSearch, setChatSearch] = useState('');
  const [chatMenuOpen, setChatMenuOpen] = useState<string | null>(null);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState<{ id: string; title: string } | null>(null);
  // Schnell loeschen: erster Klick auf den Papierkorb fragt direkt an der Zeile („Löschen?“), zweiter loescht.
  const [loeschFrage, setLoeschFrage] = useState<string | null>(null);
  useEffect(() => { if (!loeschFrage) return; const t = setTimeout(() => setLoeschFrage(null), 4000); return () => clearTimeout(t); }, [loeschFrage]);

  useEffect(() => {
    const h = (ev: MouseEvent) => {
      const t = ev.target as HTMLElement;
      if (!t.closest('[data-chat-menu]')) setChatMenuOpen(null);
      if (!t.closest('[data-header-menu]')) setHeaderMenuOpen(false);
    };
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);

  const reload = async (arch = showArchived) => onChatsChanged(await listChats(arch));
  const toggleArchived = async () => { const v = !showArchived; onShowArchivedChanged(v); setHeaderMenuOpen(false); await reload(v); };
  const archiveChat = async (c: Chat, ev: React.MouseEvent) => {
    ev.stopPropagation(); setChatMenuOpen(null);
    await patchChat(c.id, { archived: !c.archived }); await reload();
    if (activeChat?.id === c.id) onActiveChatRemoved();
  };
  const saveRename = async () => {
    if (!renaming) return;
    const t = renaming.title.trim();
    if (t) { try { await patchChat(renaming.id, { title: t }); await reload(); } catch (e) { console.warn('Rename:', e); } }
    setRenaming(null);
  };
  const removeChat = async (c: Chat, ev: React.MouseEvent) => {
    ev.stopPropagation(); setChatMenuOpen(null);
    if (!await confirm({ title: 'Chat löschen?', message: `„${chatTitleDisplay(c)}" wird mit allen Nachrichten unwiderruflich gelöscht. Falls du den Chat später wieder brauchst, lieber archivieren.`, okLabel: 'Löschen', danger: true })) return;
    try { await deleteChat(c.id); }
    catch (e: any) { await confirm({ title: 'Chat nicht gelöscht', message: e?.message ?? String(e), okLabel: 'OK' }); return; }
    if (activeChat?.id === c.id) onActiveChatRemoved();
    await reload();
  };
  const schnellLoeschen = async (c: Chat, ev: React.MouseEvent) => {
    ev.stopPropagation();
    if (loeschFrage !== c.id) { setLoeschFrage(c.id); return; }
    setLoeschFrage(null);
    try { await deleteChat(c.id); }
    catch (e: any) { await confirm({ title: 'Chat nicht gelöscht', message: e?.message ?? String(e), okLabel: 'OK' }); return; }
    if (activeChat?.id === c.id) onActiveChatRemoved();
    await reload();
  };
  const deleteAllArchived = async () => {
    setHeaderMenuOpen(false);
    if (!await confirm({ title: 'Archivierte Chats löschen?', message: 'Alle Chats im Archiv werden unwiderruflich gelöscht.', okLabel: 'Archiv leeren', danger: true })) return;
    await deleteChatsBulk('archived'); await reload();
  };
  const deleteAllChats = async () => {
    setHeaderMenuOpen(false);
    if (!await confirm({ title: 'ALLE Chats löschen?', message: 'Wirklich ALLE Chats (aktiv + archiviert) unwiderruflich löschen? Diese Aktion kann nicht rückgängig gemacht werden.', okLabel: 'Alles löschen', danger: true })) return;
    await deleteChatsBulk('all'); onActiveChatRemoved(); await reload();
  };

  const loeschbar = (c: Chat) => !c.shared_with_me_by;
  const treffer = () => filtered.filter(loeschbar);
  const alleUntenLoeschen = async () => {
    if (chatSearch.trim()) {
      const ziel = treffer();
      if (!ziel.length) return;
      if (!await confirm({ title: `${ziel.length} Chats löschen?`, message: `Alle ${ziel.length} Chats, die zur Suche „${chatSearch.trim()}" passen, werden unwiderruflich gelöscht.`, okLabel: 'Löschen', danger: true })) return;
      for (const c of ziel) { try { await deleteChat(c.id); } catch { /* geteilte o. ä. bleiben */ } }
      if (activeChat && ziel.some((c) => c.id === activeChat.id)) onActiveChatRemoved();
      await reload();
    } else await deleteAllChats();
  };

  const filtered = useMemo(() => {
    const q = chatSearch.trim().toLowerCase();
    return sortChatsWithChildren(chats.filter((c) => c.mode !== 'review' && (!q || (c.title + ' ' + (c.patient_id ?? '') + ' ' + (c.summary ?? '')).toLowerCase().includes(q))));
  }, [chats, chatSearch]);

  const gruppen = useMemo(() => {
    const raus: { label: string; icon: any; kopf: string; unter: { name: string; id: string; chats: Chat[] }[] }[] = [];
    for (const b of BEREICHE) {
      const drin = filtered.filter((c) => c.mode === b.key || (b.key === 'patient' && c.mode === 'patient_md'));
      if (!drin.length) continue;
      if (b.key === 'patient') {
        const pro = new Map<string, Chat[]>();
        for (const c of drin) { const k = patientLabel(c); pro.set(k, [...(pro.get(k) ?? []), c]); }
        raus.push({ label: b.label, icon: b.icon, kopf: b.kopf, unter: [...pro.entries()].sort((a, b2) => a[0].localeCompare(b2[0])).map(([name, cs]) => ({ name, id: praxisId(cs[0]), chats: cs })) });
      } else raus.push({ label: b.label, icon: b.icon, kopf: b.kopf, unter: [{ name: '', id: '', chats: drin }] });
    }
    return raus;
  }, [filtered]);

  const MenuItem = ({ onClick, icon: Icon, children, danger }: { onClick: (e: React.MouseEvent) => void; icon: any; children: React.ReactNode; danger?: boolean }) => (
    <button onClick={onClick} role="menuitem" className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] ${danger ? 'text-rose-700 hover:bg-rose-50' : 'text-ps-dark-700 hover:bg-gray-50'}`}><Icon className="h-3.5 w-3.5" />{children}</button>
  );

  return (<>
    {mobileSidebarOpen && <button type="button" className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => onMobileSidebarOpenChanged(false)} aria-label="Sidebar schliessen" />}
    <aside className={`card fixed inset-y-0 left-0 z-40 flex w-[300px] max-w-[85vw] flex-col overflow-hidden p-0 transition-transform duration-300 lg:static lg:z-auto lg:w-auto lg:max-w-none lg:translate-x-0 ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
      <div className="space-y-2.5 border-b border-gray-200 p-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="page-h1 flex items-baseline gap-1.5 !text-[15px]">Deine Chats <span className="numerical text-[12px] font-normal text-ps-dark-400">{chats.length}</span></h2>
          <div className="relative" data-header-menu>
            <button className="flex min-h-9 min-w-9 items-center justify-center rounded-md p-1.5 text-ps-dark-400 hover:bg-gray-100 hover:text-ps-dark-700" onClick={(e) => { e.stopPropagation(); setHeaderMenuOpen((o) => !o); }} aria-label="Chat-Optionen" aria-haspopup="menu" aria-expanded={headerMenuOpen}><EllipsisHorizontalIcon className="h-4 w-4" /></button>
            {headerMenuOpen && (
              <div data-header-menu className="card absolute right-0 top-full z-30 mt-1 w-56 max-w-[calc(100vw-1rem)] overflow-hidden p-0 shadow-xl" role="menu">
                <MenuItem onClick={toggleArchived} icon={ArchiveBoxIcon}>{showArchived ? 'Aktive Chats anzeigen' : 'Archiv anzeigen'}</MenuItem>
                <div className="h-px bg-gray-200" />
                <MenuItem onClick={deleteAllArchived} icon={ArchiveBoxXMarkIcon} danger>Archiv leeren</MenuItem>
                <MenuItem onClick={deleteAllChats} icon={TrashIcon} danger>Alle Chats löschen</MenuItem>
              </div>
            )}
          </div>
        </div>
        <button className="btn-primary flex w-full items-center justify-center gap-2" onClick={() => { setHeaderMenuOpen(false); setChatMenuOpen(null); onNewChat(); }}><PlusIcon className="h-4 w-4" /><span>Neuer Chat</span></button>
        {chats.length > 0 && (
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ps-dark-400" />
            <input type="text" value={chatSearch} onChange={(e) => setChatSearch(e.target.value)} placeholder="Chats durchsuchen …" className="input-field py-2 pl-9 pr-8 !text-[13px]" />
            {chatSearch && <button type="button" onClick={() => setChatSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-ps-dark-400 hover:text-ps-dark-900" aria-label="Suche leeren"><XMarkIcon className="h-3 w-3" /></button>}
          </div>
        )}
      </div>

      <div className="flex-1 divide-y divide-gray-200 overflow-y-auto">
        {chats.length === 0 ? (
          <div className="p-8 text-center"><div className="mb-1 text-[13px] text-ps-dark-500">Noch keine Chats</div><div className="text-[12px] leading-relaxed text-ps-dark-400">Klick auf „Neuer Chat" und wähle, was du tun möchtest.</div></div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center"><div className="mb-1 text-[13px] text-ps-dark-500">Keine Treffer</div><button onClick={() => setChatSearch('')} className="mt-3 text-[12px] text-primary-500 hover:underline">Filter zurücksetzen</button></div>
        ) : gruppen.map((g) => (
          <div key={g.label}>
            <div className={`sticky top-0 z-[5] flex items-center gap-2 border-b px-3 py-2 text-[11px] font-semibold uppercase tracking-wider ${g.kopf}`}>
              <g.icon className="h-4 w-4" />{g.label}
              <span className="numerical ml-auto font-normal normal-case tracking-normal opacity-70">{g.unter.reduce((n, u) => n + u.chats.length, 0)}</span>
            </div>
            {g.unter.map((u) => (
              <div key={u.name || '_'}>
                {u.name && (
                  <div className="flex items-center gap-2 border-l-[3px] border-primary-400 bg-white px-3 pb-1 pt-2.5">
                    <UserIcon className="h-3.5 w-3.5 shrink-0 text-primary-500" />
                    <span className="truncate text-[13px] font-semibold text-ps-dark-900">{u.name}</span>
                    {u.id && <span className="numerical shrink-0 text-[11px] text-ps-dark-400">ID {u.id}</span>}
                  </div>
                )}
                {u.chats.map((c) => {
                  const active = activeChat?.id === c.id, isRenaming = renaming?.id === c.id;
                  return (
                    <div key={c.id} role="button" tabIndex={0} onClick={() => !renaming && onSelectChat(c)} onKeyDown={(e) => { if (e.key === 'Enter' && !renaming) onSelectChat(c); }}
                      className={`group relative cursor-pointer border-t border-gray-100 transition-colors ${active ? 'bg-gray-50' : 'hover:bg-gray-50'}`} style={c.parent_chat_id ? { paddingLeft: 14 } : undefined}>
                      {active && <span className="absolute bottom-0 left-0 top-0 w-[3px] bg-primary-500" />}
                      <div className="grid grid-cols-[1fr_auto] items-start gap-2 px-3 py-2.5">
                        <div className="min-w-0">
                          {isRenaming && renaming ? (
                            <input value={renaming.title} onChange={(e) => setRenaming({ ...renaming, title: e.target.value })} onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') void saveRename(); if (e.key === 'Escape') setRenaming(null); }} onBlur={() => void saveRename()}
                              className="input-field w-full px-2 py-1 !text-[13px]" autoFocus />
                          ) : <div className="truncate text-[13px] font-medium leading-tight text-ps-dark-900">{chatTitleDisplay(c)}</div>}
                          <div className="mt-1.5 flex min-w-0 items-center gap-1.5">
                            {c.wartet_auf_antwort && <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-primary-300 bg-primary-50 px-2 py-0.5 text-[10.5px] font-semibold text-primary-600" title="Die Antwort wird gerade geschrieben"><span className="pulse-dot h-1.5 w-1.5 rounded-full bg-primary-500" />schreibt</span>}
                            <span className={`badge ${modeBadgeCls(c.mode)}`}>{modeLabel(c.mode)}</span>
                            {c.patient_id && !u.name && <span className="inline-flex min-w-0 items-center gap-1 truncate text-[11px] font-medium text-ps-dark-600"><UserIcon className="h-3 w-3 shrink-0" />{patientLabelFromId(c.patient_id)}</span>}
                            {c.archived && <ArchiveBoxIcon className="h-3 w-3 shrink-0 text-amber-700" title="Archiviert" />}
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          <span className="numerical whitespace-nowrap text-[10.5px] text-ps-dark-400">{relTime(c.updated_at)}</span>
                          {!isRenaming && (
                            <div className="flex items-center">
                            {!c.shared_with_me_by && (loeschFrage === c.id
                              ? <button className="flex min-h-9 items-center rounded bg-rose-600 px-2 text-[11px] font-semibold text-white hover:bg-rose-700" onClick={(ev) => void schnellLoeschen(c, ev)}>Löschen?</button>
                              : <button className="flex min-h-9 min-w-9 items-center justify-center rounded p-1 text-ps-dark-300 hover:bg-rose-50 hover:text-rose-600 lg:opacity-0 lg:group-hover:opacity-100 lg:focus:opacity-100" onClick={(ev) => void schnellLoeschen(c, ev)} aria-label="Chat löschen" title="Löschen"><TrashIcon className="h-4 w-4" /></button>)}
                            <div className="relative" data-chat-menu>
                              <button className="-mr-1 flex min-h-9 min-w-9 items-center justify-center rounded p-1 text-ps-dark-400 hover:bg-gray-100 hover:text-ps-dark-900" onClick={(ev) => { ev.stopPropagation(); setChatMenuOpen(chatMenuOpen === c.id ? null : c.id); }} aria-label="Optionen" aria-haspopup="menu" aria-expanded={chatMenuOpen === c.id}><EllipsisVerticalIcon className="h-3.5 w-3.5" /></button>
                              {chatMenuOpen === c.id && (
                                <div data-chat-menu className="card absolute right-0 top-7 z-10 min-w-[170px] overflow-hidden p-0 shadow-xl" role="menu">
                                  <MenuItem onClick={(e) => { e.stopPropagation(); setChatMenuOpen(null); setRenaming({ id: c.id, title: chatTitleDisplay(c) }); }} icon={PencilIcon}>Umbenennen</MenuItem>
                                  <MenuItem onClick={(e) => void archiveChat(c, e)} icon={ArchiveBoxIcon}>{c.archived ? 'Aus Archiv holen' : 'Archivieren'}</MenuItem>
                                  {!c.shared_with_me_by && <MenuItem onClick={(e) => { e.stopPropagation(); setChatMenuOpen(null); onOpenShareModal(c, e); }} icon={ShareIcon}>Teilen mit …{c.is_shared && <span className="ml-auto text-[10px] text-primary-500">geteilt</span>}</MenuItem>}
                                  <div className="h-px bg-gray-200" />
                                  {!c.shared_with_me_by && <MenuItem onClick={(e) => void removeChat(c, e)} icon={TrashIcon} danger>Löschen</MenuItem>}
                                </div>
                              )}
                            </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ))}
      </div>
      {filtered.some(loeschbar) && (
        <div className="flex items-center justify-between gap-2 border-t border-gray-200 bg-gray-50 px-3 py-2">
          <span className="text-[11px] text-ps-dark-400">{chatSearch.trim() ? `${treffer().length} Treffer` : `${chats.length} Chats`}</span>
          <button className="inline-flex min-h-9 items-center gap-1.5 rounded-md px-2.5 text-[12px] font-medium text-rose-700 hover:bg-rose-50" onClick={() => void alleUntenLoeschen()}>
            <TrashIcon className="h-4 w-4" />{chatSearch.trim() ? 'Treffer löschen' : 'Alle löschen'}
          </button>
        </div>
      )}
    </aside>
  </>);
}
